import { z } from 'zod'
import { LocalizedErrorDto } from './localizedErrorMessages'
import {
  PrimeSourceRetentionMilliseconds,
  RecipeReference,
  JobOutputFile,
} from './monocleJob'
import { vector3 } from './utils'
import {
  BundleMonocleTransformDBModel,
  BundleMonocleCameraIntrinsicDBModel,
  BundleMonocleCameraParametersDBModel,
  BundleMonocleVideoRGBDCameraModel,
  MonocleVideoPlaybackDto,
} from './monocleVideo'
import type {
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
} from 'firebase/firestore'
import type { QueryDocumentSnapshot as AdminQueryDocumentSnapshot } from 'firebase-admin/firestore'
import { getRandomUUID } from './cryptoHelper'
import { ModelUtils } from './utils'
import type { Timestamp as AdminTimestamp } from 'firebase-admin/firestore'
export const BundleKeyPathsAllowed = {
  object_scan_glb: 'metadata.object_scan.glb_filepath',
  object_scan_usdz: 'metadata.object_scan.usdz_filepath',
  object_scan_payload: 'metadata.object_scan.payload_filepath',
  object_scan_edit_zip: 'metadata.object_scan.edit_zip_filepath',
  object_scan_edit_glb: 'metadata.object_scan.glb_edit_filepath',
  object_scan_edit_usdz: 'metadata.object_scan.usdz_edit_filepath',
  monocle_video_mp4: 'metadata.monocle_video.video_filepath',
  prime_job_payload: 'metadata.prime_job.payload_filepath',
  prime_source_payload: 'metadata.prime_source.payload_filepath',
  packaged_collection_manifest:
    'metadata.packaged_collection.manifest_filepath',
}

export enum BundleDataType {
  ObjectScan = 'object_scan',
  MonocleVideo = 'monocle_video',
  Spaces = 'spaces',
  PrimeJob = 'prime_job',
  PrimeSource = 'prime_source',
  PackagedCollection = 'packaged_collection',
}
export const BundleFileMetadata = z.object({
  md5: z.string(),
  size: z.coerce.number(),
  generation: z.string().optional(),
})

export const BundlePrimeSource = z.object({
  payload_filepath: z.string().optional(),
  payload_filepath_metadata: BundleFileMetadata.optional(),
})
export const BundleJobProvenance = z.object({
  job_id: z.string(),
  recipe: RecipeReference,
  source_bundle_ids: z.array(z.string()),
  variant: z.enum(['reconstructed', 'optimized']).optional(),
})

export enum SimulateFailure {
  InternalError = 'internal_error',
  NotFoundError = 'not_found_error',
}

export enum BundleState {
  /// Bundle is not waiting for anything, should be "ready" to use
  Idle = 'idle',
  /// Bundle is waiting for a set of files to be uploaded
  WaitingForUpload = 'waiting_for_upload',
  /// Bundle is waiting to be processed
  Queued = 'queued',
  /// Bundle is currently being processed
  Processing = 'processing',
  /// Failure is unrecoverable, and should be handled by customer support or deleted by the user
  Failed = 'failed',
  /// Unknown for future if we introduced a new state that the client doesn't know about
  Unknown = 'unknown',
}

export class BundleModelFactory {
  public BundleInternalDBModel
  public createBundleInternalDBModel
  public BundleObjectScanDBModel
  public FirestoreBundleObjectScanDBModel
  public BundleMonocleTransformDBModel
  public BundleMonocleCameraIntrinsicDBModel
  public BundleMonocleCameraParametersDBModel
  public BundleMonocleVideoRGBDCameraModel
  public BundleMonocleVideoDBModel
  public BundleMonoclePrimeJobDBModel
  public BundlePackagedCollectionDBModel
  public BundleOwnerEditableDetailsDBModel
  public BundleOwnerEditableDetailsPublicDBModel
  public BundleOwnerEditableDBModel
  public FirestoreBundleOwnerEditableDBModel
  public BundleMetadataDBModel
  public FirestoreBundleMetadataDBModel
  public BundleDBModel
  public NewBundleModel
  public FirestoreBundleDBModel
  public BundleHolder
  public bundleConverter
  public adminBundleConverter
  public publicizeDefaultShareDetails
  public newBundle
  private utils

  constructor(timestamp: typeof Timestamp | typeof AdminTimestamp) {
    this.utils = new ModelUtils(timestamp)

    /**
     * Bundle Internal data management
     *
     * Workflow:
     * 1. App creates an empty Bundle definition, with a storage reference.
     * 2. App then uploads files data to Firebase Storage, with bundle id and key path reference.
     * 3. Background traigger processes the uploades, validates, and sets the document's key path.
     * 4. When all bundle's "required" paths are satisfied, then bundle_ready is set true.
     * 5. When bundle_ready, Bundle is now available to be used, listed, and viewed.
     */

    this.BundleInternalDBModel = z.object({
      required: z.array(z.string()),
      managed_by: z.literal('monocle_jobs').optional(),
      // Preserve the newer viewer preference when an older SDK replaces owner_editable.
      legacy_wall_mounted: z.boolean().optional(),
      thumbnail_job_id: z.string().optional(),
      revert_cleanup_id: z.string().optional(),
      edit_revision: z.string().optional(),
      source_upload_id: z.string().optional(),
      committed_artifacts: z.array(JobOutputFile).optional(),
      thumbnail_artifacts: z.array(JobOutputFile).optional(),
    })

    this.createBundleInternalDBModel = (
      bundle_type: BundleDataType,
    ): z.infer<typeof this.BundleInternalDBModel> => {
      const internal = {} as z.infer<typeof this.BundleInternalDBModel>

      // Set up the required parameters for the bundle_type.
      switch (bundle_type) {
        case BundleDataType.ObjectScan:
          internal.required = [
            BundleKeyPathsAllowed.object_scan_glb,
            BundleKeyPathsAllowed.object_scan_usdz,
          ]
          break
        case BundleDataType.MonocleVideo:
          internal.required = [BundleKeyPathsAllowed.monocle_video_mp4]
          break
        case BundleDataType.PrimeJob:
          internal.required = [BundleKeyPathsAllowed.prime_job_payload]
          break
        case BundleDataType.PrimeSource:
          internal.required = [BundleKeyPathsAllowed.prime_source_payload]
          internal.managed_by = 'monocle_jobs'
          break
        case BundleDataType.PackagedCollection:
          internal.required = [
            BundleKeyPathsAllowed.packaged_collection_manifest,
          ]
          break
        case BundleDataType.Spaces:
          internal.required = []
          break
      }

      return this.BundleInternalDBModel.parse(internal)
    }

    /**
     * Monocle Scan specific metadata that can be read if permissions
     * allow access to viewing the scan data.
     * (ie public == true, otherwise owner only)
     */

    this.BundleObjectScanDBModel = z.object({
      /**
       * Min bound of vertices in scan (meters)
       * [x, y, z](readonly)
       */
      min_coordinate: vector3,

      /**
       * Max bound of vertices in scan (meters)
       * [x, y, z](readonly)
       */
      max_coordinate: vector3,

      /**
       * Camera euler rotation angles (degrees)
       * Pitch (the x component) is the rotation about the x-axis.
       * Yaw (the y component) is the rotation about the y-axis.
       * Roll (the z component) is the rotation about the z-axis.
       * (default: [0, 0, 0])
       */
      camera_euler_angles: vector3.default([0, 0, 0]),

      /**
       * Camera pan (meters)
       * [x, y, z]
       * (default: [0, 0, 0])
       */
      camera_pan: vector3.optional(),

      /**
       * Payload filepath that contains the glb and usdz files for processing
       */
      payload_filepath: z.string().optional(),
      payload_filepath_metadata: BundleFileMetadata.optional(),

      /**
       * Accessible storage path when the GLB file is finished uploading
       */
      glb_filepath: z.string().optional(),
      glb_filepath_metadata: BundleFileMetadata.optional(),

      /**
       * Accessible storage path when the USDZ file is finished uploading
       */
      usdz_filepath: z.string().optional(),
      usdz_filepath_metadata: BundleFileMetadata.optional(),

      /**
       * Accessible storage path when the EDIT ZIP file is finished uploading
       * Transient Payload containing the edited GLB and USDZ files, usually null.
       * Used for atomic delivery of files into: glb_edit_filepath / usdz_edit_filepath
       */
      edit_zip_filepath: z.string().optional(),
      edit_zip_filepath_metadata: BundleFileMetadata.optional(),
      /**
       * Accessible storage path when the GLB edit file is finished uploading
       */
      glb_edit_filepath: z.string().optional(),
      glb_edit_filepath_metadata: BundleFileMetadata.optional(),
      /**
       * Accessible storage path when the USDZ edit file is finished uploading
       */
      usdz_edit_filepath: z.string().optional(),
      usdz_edit_filepath_metadata: BundleFileMetadata.optional(),

      /**
       * Carries the "modified" date from metadata on edit.zip upload
       * (see BundleFileMetadataDto)
       * If user "reverts" the edit, then this is reset to current date.
       * Fallback to metadata.created if not set
       */
      edit_modified: this.utils.date.optional(),
    })

    this.FirestoreBundleObjectScanDBModel = this.BundleObjectScanDBModel.merge(
      z.object({
        edit_modified: this.utils.dateForFirestore,
      }),
    )

    this.BundleMonocleTransformDBModel = BundleMonocleTransformDBModel
    this.BundleMonocleCameraIntrinsicDBModel =
      BundleMonocleCameraIntrinsicDBModel
    this.BundleMonocleCameraParametersDBModel =
      BundleMonocleCameraParametersDBModel
    this.BundleMonocleVideoRGBDCameraModel = BundleMonocleVideoRGBDCameraModel
    this.BundleMonocleVideoDBModel = MonocleVideoPlaybackDto.extend({
      video_filepath: z.string().optional(),
      video_filepath_metadata: BundleFileMetadata.optional(),
    })

    this.BundleMonoclePrimeJobDBModel = z.object({
      /**
       * Set flag for automatic stencil masking
       */
      auto_stencil: z.boolean().optional(),
      /**
       * Accessible storage path for prime job data
       */
      payload_filepath: z.string().optional(),

      payload_filepath_metadata: BundleFileMetadata.optional(),
      /**
       * Processing progress percentage
       * null = no longer in queue
       * -1 = not started but in queue
       * 0 = 0% to 100% processing in progress
       */
      progress: z.number().optional(),
    })

    this.BundlePackagedCollectionDBModel = z.object({
      collection_id: z.string(),
      distribution_key_id: z.string(),
      bundles: z.array(z.string()).default([]),
      manifest_filepath: z.string().optional(),
    })

    this.BundleOwnerEditableDetailsDBModel = z.object({
      title: z.string().optional(),
      email: z.string().optional(),
      description: z.string().optional(),
      location_name: z.string().optional(),
    })

    this.BundleOwnerEditableDetailsPublicDBModel = z.object({
      title: z.boolean().optional(),
      email: z.boolean().optional(),
      description: z.boolean().optional(),
      location_name: z.boolean().optional(),
    })

    this.BundleOwnerEditableDBModel = z.object({
      /**
       * Set to true when bundle should be publically acessible.
       * This auto-generates a /bundles_shared/[bundle_id]/ document
       */
      shared: z.boolean(),

      /// True when model is wall mounted, and constrains the 3D view to be foward facing only.
      wall_mounted: z.boolean().optional(),

      /**
       * Set to true when bundle should be removed from storage and database.
       */
      tombstoned: z.boolean(),

      details: this.BundleOwnerEditableDetailsDBModel,

      details_public: this.BundleOwnerEditableDetailsPublicDBModel,

      /**
       * Auto-updated date whenever a user action modifies the bundle and OE metadata.
       * Fallback to metadata.created date if not set.
       */
      modified: this.utils.date.optional(),
    })
    this.FirestoreBundleOwnerEditableDBModel =
      this.BundleOwnerEditableDBModel.extend({
        modified: this.utils.dateForFirestore,
      })

    this.publicizeDefaultShareDetails = (
      ownerEditable: z.infer<typeof this.BundleOwnerEditableDBModel>,
    ): z.infer<typeof this.BundleOwnerEditableDBModel> => {
      return this.BundleOwnerEditableDBModel.parse({
        ...ownerEditable,
        details_public: {
          ...ownerEditable.details_public,
          title: true,
          description: true,
        },
      })
    }

    this.BundleMetadataDBModel = z.object({
      /**
       * (readonly)
       * Owner of the scan
       */
      owner: z.string(),
      /**
       * (readonly)
       * Type of bundle that was created. (ex: "object_scan")
       */
      data_type: z.nativeEnum(BundleDataType),
      /**
       * (readonly)
       * Bundle created timestamp on document creation,
       * used for purging stale bundles that don't become
       * "bundle_ready" within 24 hours.
       */
      server_created: this.utils.date,
      auto_delete_at: this.utils.date.optional(),
      /**
       * (readonly)
       * Bundle is ready for consumption from completing sufficient file uploads
       * to satisfy the "internal.required" array.
       *
       * If Bundle is not marked ready for > 24 hours, then automatically remove the bundle from storage.
       */
      ready: z.boolean(),
      /**
       * (readonly)
       * Current state of the bundle
       * Idle -> WaitingForUpload -> Queued -> Processing -> Idle / Failure
       */
      state: z.nativeEnum(BundleState).optional(),
      /**
       * (readonly)
       * timestamp of when bundle was originally created by the app
       * (cannot be newer than now, or older than 1970-01-01 00:00:00 UTC)
       */
      created: this.utils.date,
      /**
       * (readonly)
       * Size of bundle data in bytes.
       */
      size: z.number().optional(),
      /**
       * (editable by owner)
       * Poster thumbnail of bundle
       *
       * Format:
       * - JPG encoding
       * - limited to 64KB
       * - resolution under 1024x768
       *   (@2x retina 512x384)
       * - aspect ratio 4:3
       * - Generally size is 640x480
       */
      poster_jpg_data: this.utils.base64String,
      /**
       * (editable by owner)
       * Poster thumbnail mask of bundle
       *
       * Format:
       * - PNG encoding
       * - Limited to 64KB
       * - Matching resolution to poster_jpg_data
       */
      poster_mask_png_data: this.utils.base64String.optional(),
      /**
       * (readonly)
       * The client ID is client side ID, that gives a client context for when it comes back
       */
      client_id: z.string().optional(),
      /**
       * (readonly)
       * The thread ID is the related set of created bundles that share common source data.
       */
      thread_id: z.string().optional(),
      /**
       * (editable by owner)
       * List of error messages appended when processing, localized to the user's current locale.
       * Optionally cleared by the owner (deprecated, but left here for Monocle Structure Scanner)
       */
      error_messages: z.array(z.string()),
      /**
       * (readonly)
       * Localizable error message with parameters set when processing a bundle upload, storage, or processing error.
       * (ex: "OVER_QUOTA_OBJECT_SCAN", {quota: 50})
       */
      localized_error: LocalizedErrorDto.optional(),
      /**
       * Object scanner specific metadata that can be read if permissions
       * allow access to viewing the scan data.
       * (ie public == true, otherwise owner only)
       */
      object_scan: this.BundleObjectScanDBModel.optional(),
      /**
       * Monocle Video metadata.
       */
      monocle_video: this.BundleMonocleVideoDBModel.optional(),
      /**
       * Monocle Prime Job metadata.
       */
      prime_job: this.BundleMonoclePrimeJobDBModel.optional(),
      prime_source: BundlePrimeSource.optional(),
      job_provenance: BundleJobProvenance.optional(),
      /**
       * Packaged Collection metadata.
       */
      packaged_collection: this.BundlePackagedCollectionDBModel.optional(),
    })

    this.FirestoreBundleMetadataDBModel = this.BundleMetadataDBModel.extend({
      server_created: this.utils.dateForFirestore,
      auto_delete_at: this.utils.dateForFirestore,
      created: this.utils.dateForFirestore,
      poster_jpg_data: this.utils.bytesForFirestore,
      poster_mask_png_data: this.utils.bytesForFirestore.optional(),
      object_scan: this.FirestoreBundleObjectScanDBModel.optional(),
    })

    this.BundleDBModel = z.object({
      id: z.string(),
      /**
       * (readonly)
       * Semantic version of data this bundle record, default: "1.0.0"
       */
      version: z.string(),
      /**
       * Data common to all bundles, and read-only by owner (no changing after creation)
       */
      metadata: this.BundleMetadataDBModel,
      /**
       * (readonly by owner)
       * Internal data used for storage management and background processor.
       * Initially it can't exist, because we need the generated BundleID to set-up storage.
       */
      internal: this.BundleInternalDBModel.optional(),
      /**
       * (owner only)
       * Owner level editable metadata, anchors and contextual information.
       */
      owner_editable: this.BundleOwnerEditableDBModel,
      /**
       * Optional flag to simulate standard bundle processing errors
       */
      simulate_failure: z.nativeEnum(SimulateFailure).optional(),
    })

    this.NewBundleModel = this.BundleDBModel.omit({ id: true })

    this.FirestoreBundleDBModel = this.BundleDBModel.omit({ id: true }).extend({
      metadata: this.FirestoreBundleMetadataDBModel,
      owner_editable: this.FirestoreBundleOwnerEditableDBModel,
    })

    //@ts-expect-error: this is a circular reference
    this.newBundle = (owner: string, dto) => {
      const serverCreated = new Date()
      const internal = this.createBundleInternalDBModel(dto.data_type)
      const owner_editable = this.publicizeDefaultShareDetails(
        this.BundleOwnerEditableDBModel.parse(dto.owner_editable),
      )
      const metadata = this.BundleMetadataDBModel.parse({
        ...dto,
        owner: owner,
        server_created: serverCreated,
        ...(dto.data_type === BundleDataType.PrimeSource
          ? {
              auto_delete_at: new Date(
                serverCreated.getTime() + PrimeSourceRetentionMilliseconds,
              ),
              prime_source: {},
            }
          : {}),
        ready: false,
        state: BundleState.WaitingForUpload,
        size: 0,
        poster_jpg_data: dto.poster_jpg_base64,
        ...(dto.poster_mask_png_base64 !== undefined
          ? { poster_mask_png_data: dto.poster_mask_png_base64 }
          : {}),
        client_id: dto.client_id ?? getRandomUUID(),
        thread_id: dto.thread_id ?? getRandomUUID(),
        error_messages: [],
        // localized_error
      })
      const bundle = this.NewBundleModel.parse({
        version: '1.0.0',
        metadata: metadata,
        internal: internal,
        owner_editable: owner_editable,
      })
      return bundle
    }

    /**
     * Bundle holder for the bundle id and bundle data contents.
     */

    this.BundleHolder = z.object({
      bundle_id: z.string(),
      bundle: this.BundleDBModel,
    })

    type BundleDBModel = z.infer<typeof this.BundleDBModel>
    type FirestoreBundleDBModel = z.infer<typeof this.FirestoreBundleDBModel>

    this.bundleConverter = {
      toFirestore: (bundle: BundleDBModel): FirestoreBundleDBModel => {
        return this.FirestoreBundleDBModel.parse(bundle)
      },

      fromFirestore: (
        snapshot: QueryDocumentSnapshot<BundleDBModel, FirestoreBundleDBModel>,
        options: SnapshotOptions,
      ): BundleDBModel => {
        const data = snapshot.data(options)
        const returnDat = this.BundleDBModel.parse({
          ...data,
          id: snapshot.id,
        })
        return returnDat
      },
    }
    this.adminBundleConverter = {
      toFirestore: (bundle: BundleDBModel) => {
        return this.FirestoreBundleDBModel.parse(bundle)
      },

      fromFirestore: (
        snapshot: AdminQueryDocumentSnapshot<BundleDBModel>,
      ): BundleDBModel => {
        const data = snapshot.data()!
        const returnDat = this.BundleDBModel.parse({
          ...data,
          id: snapshot.id,
        })
        return returnDat as BundleDBModel
      },
    }
  }
}
